import { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Linking } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import TiptapEditor from '@/components/General/inputs/QuillEditor';
import usePlatform from '@/utils/hooks/usePlatform';
import Unicon from './Unicon';
import Toast from 'react-native-toast-message';
import { sendEmail } from '@/data/emails';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import RecipientList from './RecipientList';
import SchedulePicker, { getScheduledDate } from './SchedulePicker';

const EMAIL_METHODS = [
    {
        id: 'device',
        name: 'Device Email',
        description: 'Opens your device\'s default email app (Outlook, Apple Mail, etc.). Useful for quick emails that you want to come from your personal email.',
        icon: 'mail',
        onlyNative: false,
    },
    {
        id: 'simple',
        name: 'Simple Text Email',
        description: 'Send a simple text email with subject and body, including mail merge variables. Great for quick & personalized emails from the organization, church updates, or simple notifications that might need a few more design options.',
        icon: 'document-text',
        onlyNative: false,
    },
    {
        id: 'editor',
        name: 'Email Builder',
        description: 'Design rich email templates with drag-and-drop editor. Detailed templating for professional-looking emails. Great for newsletters, announcements, or promotional materials.',
        icon: 'build',
        onlyNative: false,
        disabled: false,
    },
];

const AVAILABLE_VARIABLES = [
    { key: '{{firstName}}', label: 'First Name' },
    { key: '{{lastName}}', label: 'Last Name' },
    { key: '{{fullName}}', label: 'Full Name' },
    { key: '{{email}}', label: 'Email Address' },
    { key: '{{phone}}', label: 'Phone Number' },
];

const EmailModal = ({ isOpen, onClose, recipients }) => {
    const { isNative, isWeb } = usePlatform();
    const queryClient = useQueryClient();
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [scheduleMode, setScheduleMode] = useState('now');
    const [scheduledFor, setScheduledFor] = useState(null);
    const quillRef = useRef(null);
    const [localRecipients, setLocalRecipients] = useState(recipients);

    useEffect(() => {
        if (isOpen) {
            setLocalRecipients(recipients);
            setScheduleMode('now');
            setScheduledFor(null);
        }
    }, [isOpen, recipients]);

    const handleRemoveRecipient = (id) => {
        setLocalRecipients(prev => prev.filter(r => r.id !== id));
    };

    const filteredMethods = EMAIL_METHODS.filter(method => {
        if (method.disabled) return false;
        if (method.onlyNative && !isNative) return false;
        return true;
    });

    const handleMethodSelect = (methodId) => {
        if (methodId === 'editor') {
            if (isWeb) {
                // Close modal and navigate to editor
                const recipientIDs = localRecipients.map(p => p.id);
                onClose();
                router.push({
                    pathname: '/emails/editor',
                    params: { recipientIDs: JSON.stringify(recipientIDs) }
                });
            } else {
                // Show message that editor is web-only
                Toast.show({
                    type: 'info',
                    text2: 'The email editor is available on the web app. Visit app.reachchms.com to design emails.'
                });
            }
            return;
        }

        setSelectedMethod(methodId);
        if (methodId === 'device') {
            handleDeviceEmail();
        }
    };

    const handleDeviceEmail = () => {
        const emails = localRecipients.map(p => p.email).filter(Boolean);
        if (emails.length === 0) {
            Toast.show({ type: 'error', text2: 'No email addresses found for selected people.' });
            return;
        }
        Linking.openURL(`mailto:${emails.join(',')}`).catch(() => {
            Toast.show({ type: 'error', text2: 'No email app found on this device.' });
        });
        onClose();
    };

    const handleSimpleEmail = async () => {
        const scheduledDate = getScheduledDate(scheduleMode, scheduledFor);
        if (scheduleMode === 'later' && !scheduledDate) {
            Toast.show({ type: 'error', text2: 'Choose a future date and time to send this email.' });
            return;
        }
        try {
            const recipientIDs = localRecipients.map(p => p.id);
            await sendEmail({
                type: 'simple',
                subject,
                recipientIDs,
                body,
                scheduledFor: scheduledDate?.toISOString(),
            });
            queryClient.invalidateQueries({ queryKey: QueryKeys.emailCampaigns.all() });
            onClose();
        } catch (error) {
            console.error('Error sending email:', error);
        }
    };

    const insertVariable = (variable) => {
        if (isWeb && quillRef.current) {
            const editor = quillRef.current.getEditor();
            editor.chain().focus().insertContent(variable).run();
            setBody(editor.getHTML());
        } else {
            setBody(prev => prev + variable);
        }
    };

    const handleModalClose = () => {
        onClose();
    };

    const renderMethodSelection = () => (
        <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
            <RecipientList recipients={localRecipients} mode="email" onRemove={handleRemoveRecipient} />

            <View style={styles.methodsContainer}>
                {filteredMethods.map((method) => (
                    <TouchableOpacity
                        key={method.id}
                        style={[styles.methodCard, method.disabled && styles.methodCardDisabled]}
                        onPress={() => !method.disabled && handleMethodSelect(method.id)}
                        disabled={method.disabled}
                    >
                        <View style={styles.methodIcon}>
                            <Unicon name={method.icon} size={24} colorVariant={method.disabled ? 'textTertiary' : 'primary'} />
                        </View>
                        <View style={styles.methodContent}>
                            <Text style={[styles.methodName, method.disabled && styles.methodNameDisabled]}>
                                {method.name}
                            </Text>
                            <Text style={[styles.methodDescription, method.disabled && styles.methodDescriptionDisabled]}>
                                {method.description}
                            </Text>
                        </View>
                        <Unicon name="chevron-forward" size={18} colorVariant={method.disabled ? 'textTertiary' : 'textSecondary'} />
                    </TouchableOpacity>
                ))}
            </View>
        </ScrollView>
    );

    const renderSimpleEmailForm = () => (
        <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
            <TouchableOpacity style={styles.backButton} onPress={() => setSelectedMethod(null)}>
                <Unicon name="chevron-back" size={20} colorVariant="primary" />
                <Text style={styles.backButtonText}>Back to methods</Text>
            </TouchableOpacity>

            <RecipientList recipients={localRecipients} mode="email" onRemove={handleRemoveRecipient} />

            <SchedulePicker
                testID="email-send-schedule"
                scheduleMode={scheduleMode}
                scheduledFor={scheduledFor}
                onScheduleChange={({ scheduleMode: mode, scheduledFor: date }) => {
                    if (mode) setScheduleMode(mode);
                    if (date !== undefined) setScheduledFor(date);
                }}
                nowLabel="Send Now"
                laterLabel="Send Later"
                pickerTitle="Send Email At"
                colorVariant="primary"
            />

            <Input
                title="Subject"
                value={subject}
                onUpdate={setSubject}
                placeholder="Enter email subject..."
            />

            <View style={styles.bodySection}>
                <View style={styles.bodyHeader}>
                    <Text style={styles.bodyTitle}>Email Body</Text>
                    <Text style={styles.bodyHint}>
                        You can use variables like {'{{firstName}}'} that will be replaced with recipient data
                    </Text>
                </View>

                {isWeb ? (
                    <TiptapEditor
                        ref={quillRef}
                        value={body}
                        onChange={setBody}
                        placeholder="Write your email message here..."
                    />
                ) : (
                    <Input
                        multiline
                        value={body}
                        onUpdate={setBody}
                        placeholder="Write your email message here..."
                        style={styles.bodyInput}
                    />
                )}

                <View style={styles.variablesSection}>
                    <Text style={styles.variablesTitle}>Available Variables</Text>
                    <View style={styles.variablesList}>
                        {AVAILABLE_VARIABLES.map((variable) => (
                            <TouchableOpacity
                                key={variable.key}
                                style={styles.variableChip}
                                onPress={() => insertVariable(variable.key)}
                            >
                                <Text style={styles.variableKey}>{variable.key}</Text>
                                <Text style={styles.variableLabel}>{variable.label}</Text>
                            </TouchableOpacity>
                        ))}
                    </View>
                </View>
            </View>
        </ScrollView>
    );

    return (
        <>
            <SheetModal
                isOpen={isOpen}
                onClose={handleModalClose}
                title="Send Email"
                cancelText="Cancel"
                confirmText={selectedMethod === 'simple' ? (scheduleMode === 'later' ? 'Schedule' : 'Send') : undefined}
                onConfirm={selectedMethod === 'simple' ? handleSimpleEmail : undefined}
            >
                {selectedMethod === null ? renderMethodSelection() : renderSimpleEmailForm()}
            </SheetModal>
        </>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    methodsContainer: {
        gap: 12,
    },
    methodCard: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        backgroundColor: colors.surface,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border,
        gap: 12,
        _web: {
            _hover: {
                backgroundColor: colors.border,
            },
        },
    },
    methodCardDisabled: {
        opacity: 0.5,
    },
    methodIcon: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    methodContent: {
        flex: 1,
    },
    methodName: {
        fontSize: 16,
        fontFamily: fonts.heading,
        color: colors.text,
        marginBottom: 4,
    },
    methodNameDisabled: {
        color: colors.textTertiary,
    },
    methodDescription: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 18,
    },
    methodDescriptionDisabled: {
        color: colors.textTertiary,
    },
    backButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginBottom: 20,
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: colors.surface,
        borderRadius: 8,
        alignSelf: 'flex-start',
        _web: {
            transition: 'background-color 0.2s',
            _hover: {
                backgroundColor: colors.hover,
            }
        }
    },
    backButtonText: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.primary,
    },
    bodySection: {
        marginTop: 8,
    },
    bodyHeader: {
        marginBottom: 8,
    },
    bodyTitle: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
        marginBottom: 4,
    },
    bodyHint: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        fontStyle: 'italic',
    },
    bodyInput: {
        minHeight: 150,
    },
    variablesSection: {
        marginTop: 16,
    },
    variablesTitle: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
        marginBottom: 12,
    },
    variablesList: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    variableChip: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 8,
        backgroundColor: colors.surface,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: colors.border,
        gap: 6,
    },
    variableKey: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    variableLabel: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
}));

export default EmailModal;
